import React, { useState, useRef } from 'react';
import api from '../../config/axiosConfig';
import { API_URLS } from '../../config/Constants';
import { sessionManager } from '../../config/sessionManager';
import UploadResultModal from './UploadResultModal';
import DataReviewModal from './DataReviewModal';

interface RosterUploadProps {
  onUploadComplete?: () => void;
  onClose?: () => void;
}

interface UploadResult {
  playersAdded: number;
  playersSkipped: number;
  errors: string[];
  processedData: Array<{
    rowNumber: number;
    status: 'success' | 'skipped' | 'error';
    data: any;
    error?: string;
  }>;
}

const RosterUpload: React.FC<RosterUploadProps> = ({ onUploadComplete, onClose }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [showDataModal, setShowDataModal] = useState(false);
  const [uploadResult, setUploadResult] = useState<{
    success: boolean;
    message: string;
    data?: UploadResult;
    errors?: string[];
  } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const clientId = sessionManager.getClientId();

  const validateAndSetFile = (file: File) => {
    // Validate file type
    const allowedTypes = ['.csv', '.xlsx', '.xls'];
    const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();

    if (!allowedTypes.includes(fileExtension)) {
      setUploadResult({
        success: false,
        message: "This file type isn't supported. Please choose a CSV or Excel file (.csv, .xlsx, .xls)",
      });
      setShowResultModal(true);
      return false;
    }

    // Validate file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      setUploadResult({
        success: false,
        message: 'This file is too large. Please choose a file smaller than 10MB.',
      });
      setShowResultModal(true);
      return false;
    }

    setSelectedFile(file);
    return true;
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set dragOver to false if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      validateAndSetFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setUploadResult({
        success: false,
        message: 'Please choose a file to upload first',
      });
      setShowResultModal(true);
      return;
    }

    if (!clientId) {
      setUploadResult({
        success: false,
        message: 'Unable to identify your account. Please refresh the page and try again.',
      });
      setShowResultModal(true);
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('clientId', clientId);

      const response = await api.post(API_URLS.UPLOAD_ROSTER, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      if (response.data.success) {
        setUploadResult({
          success: true,
          message: response.data.message,
          data: response.data.data,
          errors: response.data.data.errors || [],
        });
        setSelectedFile(null);
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        // Skip result modal and go directly to data review modal
        setShowDataModal(true);
        if (onUploadComplete) {
          onUploadComplete();
        }
      } else {
        setUploadResult({
          success: false,
          message: response.data.message || 'Upload failed. Please check your file and try again.',
          data: response.data.data, // Include processed data even for failed uploads
          errors: response.data.data?.errors || [],
        });
        // Skip result modal and go directly to data review modal
        setShowDataModal(true);
      }
    } catch (error: any) {
      console.error('Upload error:', error);
      setUploadResult({
        success: false,
        message: error.response?.data?.message || 'Something went wrong while uploading. Please try again.',
        data: {
          playersAdded: 0,
          playersSkipped: 0,
          errors: [],
          processedData: [],
        }, // Empty data for error cases
        errors: [],
      });
      // For errors, we might not have processed data, so show result modal
      setShowResultModal(true);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get(API_URLS.DOWNLOAD_ROSTER_TEMPLATE, {
        responseType: 'blob',
        headers: {
          Authorization: `Bearer ${sessionManager.getToken()}`,
        },
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'Template-Players_Roster.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const handleViewDetails = () => {
    setShowResultModal(false);
    setShowDataModal(true);
  };

  const handleCloseDataModal = () => {
    setShowDataModal(false);
    // Close the parent upload modal when data modal is closed
    if (onClose) {
      onClose();
    }
  };

  const clearFile = () => {
    setSelectedFile(null);
    setUploadResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 w-full">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800 flex items-center">
          <svg className="w-6 h-6 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>
          Upload Roster
        </h2>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Template Download Section */}
      <div className="bg-blue-50 rounded-lg p-4 mb-6">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-800 mb-2">Download Roster Template</h3>
            <p className="text-sm text-blue-700 mb-3">
              <strong>Use the template below to ensure correct formatting.</strong> All fields are required: Player
              Number, First Name, Last Name, Position, Graduation Year
            </p>
            <p className="text-sm text-blue-700 mb-2">
              <strong>Header names must match EXACTLY</strong> - including capitalization, spacing, and punctuation.{' '}
              <strong>Need help?</strong> - We're here to help! Please contact{' '}
              <a href="mailto:support@statdog.live" className="underline">
                support@statdog.live
              </a>{' '}
              for assistance.
            </p>
            <p className="text-sm text-blue-600 mb-3">
              <strong>Positions:</strong> Attack, Midfield, Defense, Face-off, Goalie, Long Stick Midfield, Short Stick
              Defensive Midfield (comma-separated for multiple)
            </p>
            <p className="text-sm text-blue-700 mb-2">
              <strong>Important:</strong> The positions should match exactly with the list above - including
              capitalization and spacing. Invalid positions will be rejected during upload.
            </p>
            <button
              onClick={handleDownloadTemplate}
              className="inline-flex items-center px-3 py-2 border border-blue-300 rounded-md text-sm font-medium text-blue-700 bg-white hover:bg-blue-50 transition-colors"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              Download Template
            </button>
          </div>
        </div>
      </div>

      {/* File Upload Section */}
      <div className="space-y-4">
        {!selectedFile && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Select Roster File</label>
            <div
              className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed rounded-md transition-colors ${
                isDragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <div className="space-y-1 text-center">
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                  <path
                    d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <div className="flex text-sm text-gray-600">
                  <label
                    htmlFor="roster-file-upload"
                    className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                  >
                    <span>Upload a file</span>
                    <input
                      id="roster-file-upload"
                      ref={fileInputRef}
                      name="roster-file-upload"
                      type="file"
                      className="sr-only"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileSelect}
                    />
                  </label>
                  <p className="pl-1">or drag and drop</p>
                </div>
                <p className="text-xs text-gray-500">CSV, XLSX, XLS up to 10MB</p>
              </div>
            </div>
          </div>
        )}

        {selectedFile && (
          <div className="bg-gray-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <div>
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                </div>
              </div>
              <button
                onClick={clearFile}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Remove file"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Upload Button */}
        <div className="flex justify-end space-x-3">
          {onClose && (
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
              disabled={uploading}
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors flex items-center"
          >
            {uploading ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Uploading...
              </>
            ) : (
              'Upload Roster'
            )}
          </button>
        </div>
      </div>

      {/* Upload Result Modal */}
      {showResultModal && uploadResult && (
        <UploadResultModal
          isOpen={showResultModal}
          onClose={() => {
            setShowResultModal(false);
            // Close the parent upload modal when result modal is closed
            if (onClose) {
              onClose();
            }
          }}
          success={uploadResult.success}
          title="Roster Upload"
          message={uploadResult.message}
          errors={uploadResult.errors}
          data={uploadResult.data}
          onViewDetails={
            uploadResult.data?.processedData && uploadResult.data.processedData.length > 0
              ? handleViewDetails
              : undefined
          }
        />
      )}

      {/* Data Review Modal */}
      {showDataModal && uploadResult?.data?.processedData && (
        <DataReviewModal
          isOpen={showDataModal}
          onClose={handleCloseDataModal}
          title="Roster Upload"
          type="roster"
          processedData={uploadResult.data.processedData}
          isValidationOnly={!uploadResult.success}
        />
      )}
    </div>
  );
};

export default RosterUpload;
